/* ─────────────────────────────────────────────────────────────
   CatalogueSearchSelectField — search + select against a server catalogue (ENT-565 FR-67).

   The options are not in the config: they are read page by page from the catalogue query the
   proposal module already has (`useCatalogueOptions`), matched on the SERVER by the search text,
   and filtered by the chosen parent when the catalogue has one. The field holds the chosen
   option's KEY (an id), never its label, and reports the whole option through `onOptionChosen`.

   Config (field.props):
   - catalogueKind (string)  — which catalogue: JURISDICTION, LEGAL_TYPE, PACKAGE, ACTIVITY, PRODUCT.
   - parentField (string, optional) — the sibling field whose value filters this catalogue. Read from
     runtimeContext[parentField], which FormBuilder fills with live form values. Until it has a
     value the selector is disabled and says so.
   ───────────────────────────────────────────────────────────── */

import { useState } from 'react';
import type { FieldDisplayProps, FieldRendererProps } from '../../config/types';
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox';
import { Text } from '@/components/ui/Text';
import { useCachedCatalogueLabel, useCatalogueOptions } from '@/lib/proposals/use-wizard';
import type { CatalogueKind } from '@/lib/proposals/wizard-model';

const CATALOGUE_KINDS: readonly string[] = ['JURISDICTION', 'LEGAL_TYPE', 'PACKAGE', 'ACTIVITY', 'PRODUCT'];

/** What came with the chosen id: the option the server listed. */
export type CatalogueChoice = {
  key: string;
  label: string;
  price: string | null;
  defaultTaxSetting: string | null;
};

export interface CatalogueSelectControlProps {
  id?: string;
  value: string;
  kind: string | undefined;
  /** The parent's value, or `undefined` when the catalogue has no parent. */
  parentKey?: string;
  /** True when the catalogue is filtered by a parent, so the selector waits for one. */
  needsParent: boolean;
  disabled?: boolean;
  invalid?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  onChange: (key: string, choice: CatalogueChoice | null) => void;
}

export function CatalogueSelectControl({
  id,
  value,
  kind,
  parentKey,
  needsParent,
  disabled,
  invalid,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  onChange,
}: CatalogueSelectControlProps) {
  const [search, setSearch] = useState('');
  const knownKind = kind !== undefined && CATALOGUE_KINDS.includes(kind) ? (kind as CatalogueKind) : null;
  const blocked = knownKind === null || (needsParent && !parentKey);
  const query = useCatalogueOptions({
    kind: knownKind ?? 'JURISDICTION',
    parentKey: needsParent ? parentKey : undefined,
    search,
    enabled: !blocked,
  });
  const items = query.data ?? [];
  // The chosen option is not always in the list on screen: the list follows the search box (cleared
  // again on every pick) and holds only the first page. Its name is kept from the moment it was
  // seen, so the trigger shows the name and not the key.
  const [chosen, setChosen] = useState<{ key: string; label: string } | null>(null);
  const cachedLabel = useCachedCatalogueLabel(knownKind ?? 'JURISDICTION');
  const comboOptions: ComboboxOption[] = items.map((i) => ({
    value: i.key,
    label: i.label,
    description: i.code && i.code !== i.label ? i.code : undefined,
    skipFilter: true,
  }));
  if (value && !comboOptions.some((o) => o.value === value)) {
    const remembered = chosen?.key === value ? chosen.label : undefined;
    comboOptions.unshift({ value, label: remembered ?? cachedLabel(value) ?? value, skipFilter: true });
  }
  return (
    <Combobox
      id={id}
      mode="single"
      options={comboOptions}
      value={value}
      disabled={disabled || blocked}
      clearable
      placeholder={blocked && needsParent ? 'Choose the selection above first' : 'Select…'}
      searchPlaceholder="Search…"
      emptyMessage={query.isError ? 'The options could not be loaded' : query.isLoading ? 'Loading…' : 'No matches'}
      onSearch={setSearch}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      ariaDescribedBy={invalid ? ariaDescribedBy : undefined}
      onValueChange={(key) => {
        const hit = items.find((i) => i.key === key);
        setChosen(hit ? { key: hit.key, label: hit.label } : null);
        onChange(
          key,
          hit ? { key: hit.key, label: hit.label, price: hit.price ?? null, defaultTaxSetting: hit.defaultTaxSetting ?? null } : null,
        );
      }}
    />
  );
}

export function CatalogueSearchSelectRenderer({ field, value, onChange, onBlur, error, runtimeContext, onOptionChosen }: FieldRendererProps) {
  const kind = typeof field.props?.catalogueKind === 'string' ? field.props.catalogueKind : undefined;
  const parentField = typeof field.props?.parentField === 'string' && field.props.parentField !== '' ? field.props.parentField : undefined;
  const parent = parentField ? runtimeContext[parentField] : undefined;
  return (
    <CatalogueSelectControl
      id={field.id}
      value={typeof value === 'string' ? value : ''}
      kind={kind}
      needsParent={parentField !== undefined}
      parentKey={typeof parent === 'string' && parent !== '' ? parent : undefined}
      disabled={field.isDisabled}
      invalid={!!error}
      ariaLabel={field.label}
      ariaDescribedBy={`${field.id}-issues`}
      onChange={(key, choice) => {
        onChange(key);
        onOptionChosen?.(choice);
        onBlur();
      }}
    />
  );
}

export function CatalogueSearchSelectDisplay({ field, value }: FieldDisplayProps) {
  const kind = typeof field.props?.catalogueKind === 'string' && CATALOGUE_KINDS.includes(field.props.catalogueKind) ? (field.props.catalogueKind as CatalogueKind) : 'JURISDICTION';
  const labelOf = useCachedCatalogueLabel(kind);
  const key = typeof value === 'string' ? value : '';
  if (key === '') return <Text variant="body-md" color="muted">—</Text>;
  return <Text variant="body-md">{labelOf(key) ?? key}</Text>;
}
